import "./Portfolio.css";

import React, { useState, useEffect, useMemo } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import PortfolioNavbar from "../portfolio-components/PortfolioNavbar";
import { Helmet } from "react-helmet";
import { LineChart } from "../portfolio-components/LineChart";
import { useTransactions } from "../../lib/useTransactions";
import { useFxRates, getBaseCurrency } from "../../lib/fx";
import { cachedFetch, TTL } from "../../lib/clientCache";
import {
  buildPortfolioValueSeries,
  buildFlows,
  buildTwrIndex,
  buildBenchmarkIndex,
} from "../../lib/performance";

const TITLE = "Performance";

const RANGES = [
  { value: "3mo", label: "3M" },
  { value: "6mo", label: "6M" },
  { value: "1y", label: "1Y" },
  { value: "2y", label: "2Y" },
  { value: "5y", label: "5Y" },
  { value: "max", label: "Max" },
];

const BENCHMARKS = [
  { value: "", label: "No benchmark" },
  { value: "^GSPC", label: "S&P 500" },
  { value: "^NDX", label: "Nasdaq 100" },
  { value: "^FTSE", label: "FTSE 100" },
  { value: "VWRL.L", label: "FTSE All-World (VWRL)" },
];

function PerformanceView() {
  const { transactions, loading: txLoading } = useTransactions();
  const { fx } = useFxRates(getBaseCurrency());

  const [range, setRange] = useState("1y");
  const [benchmark, setBenchmark] = useState("^GSPC");
  const [histories, setHistories] = useState(null);
  const [benchmarkHistory, setBenchmarkHistory] = useState(null);
  const [error, setError] = useState(null);

  // Load portfolio tickers, then their price histories (all client-cached).
  useEffect(() => {
    let cancelled = false;
    setHistories(null);
    setError(null);

    (async () => {
      try {
        const fns = getFunctions();
        const portfolio = await cachedFetch("portfolio", TTL.PORTFOLIO, async () => {
          const res = await httpsCallable(fns, "getPortfolio")();
          return JSON.parse(res.data);
        });
        const symbols = (Object.entries(portfolio) || []).map(([, v]) => {
          const ticker = Object.keys(v)[0];
          return { ticker, exchange: v[ticker]?.exchange || "" };
        });
        if (symbols.length === 0) {
          if (!cancelled) setHistories({});
          return;
        }
        const tickerKey = symbols.map((s) => s.ticker).sort().join(",");
        const result = await cachedFetch(`history:${range}:${tickerKey}`, TTL.HISTORY, async () => {
          const res = await httpsCallable(fns, "getPriceHistory")({ symbols, range });
          return JSON.parse(res.data);
        });
        if (!cancelled) setHistories(result);
      } catch (err) {
        console.error("Performance history fetch error:", err);
        if (!cancelled) setError(err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [range]);

  // Benchmark history (separate so switching benchmark doesn't refetch holdings).
  useEffect(() => {
    if (!benchmark) { setBenchmarkHistory(null); return undefined; }
    let cancelled = false;
    setBenchmarkHistory(null);
    cachedFetch(`history:${range}:bench:${benchmark}`, TTL.HISTORY, async () => {
      const res = await httpsCallable(getFunctions(), "getPriceHistory")({
        symbols: [{ ticker: benchmark, exchange: benchmark.includes(".") ? "XLON" : "" }],
        range,
      });
      return JSON.parse(res.data);
    })
      .then((parsed) => { if (!cancelled) setBenchmarkHistory(parsed[benchmark] || null); })
      .catch((err) => console.error("Benchmark fetch error:", err));
    return () => { cancelled = true; };
  }, [benchmark, range]);

  const valueSeries = useMemo(
    () => (histories && transactions ? buildPortfolioValueSeries(histories, transactions, fx) : []),
    [histories, transactions, fx]
  );

  const twrSeries = useMemo(() => {
    if (valueSeries.length === 0 || !transactions) return [];
    return buildTwrIndex(valueSeries, buildFlows(transactions, fx));
  }, [valueSeries, transactions, fx]);

  // Rebase the benchmark to 100 at the start of the portfolio series so the
  // two lines are directly comparable.
  const benchmarkSeries = useMemo(() => {
    if (!benchmarkHistory || twrSeries.length === 0) return [];
    const raw = buildBenchmarkIndex(benchmarkHistory).filter((p) => p.ts >= twrSeries[0].ts);
    if (raw.length === 0) return [];
    const first = raw[0].index;
    return raw.map((p) => ({ ts: p.ts, index: (p.index / first) * 100 }));
  }, [benchmarkHistory, twrSeries]);

  const loading = txLoading || histories === null;
  const benchmarkLabel = BENCHMARKS.find((b) => b.value === benchmark)?.label;

  return (
    <div className="PerformanceView">
      <Helmet>
        <title>{TITLE}</title>
      </Helmet>
      <div className="row">
        <div className="sidebar col-md-auto">
          <PortfolioNavbar />
        </div>

        <div className="main-content col">
          <h2>Performance</h2>

          <div className="d-flex gap-3 align-items-end mb-3 flex-wrap">
            <div>
              <label className="form-label mb-1">Range</label>
              <div className="btn-group btn-group-sm" role="group">
                {RANGES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    className={`btn btn-outline-primary${range === r.value ? " active" : ""}`}
                    onClick={() => setRange(r.value)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="form-label mb-1" htmlFor="benchmark-select">Benchmark</label>
              <select
                id="benchmark-select"
                className="form-select form-select-sm"
                value={benchmark}
                onChange={(e) => setBenchmark(e.target.value)}
              >
                {BENCHMARKS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
          </div>

          {error && <p className="text-danger">Error: {error}</p>}
          {loading && !error && <p>Loading price history...</p>}

          {!loading && !error && (
            <>
              <div className="card mb-3">
                <div className="card-header fw-bold">
                  Total Return vs Benchmark (indexed to 100)
                </div>
                <div className="card-body">
                  <p className="text-muted small mb-2">
                    Time-weighted return: deposits and withdrawals are stripped out, so this
                    line is directly comparable with {benchmarkLabel || "a benchmark"}.
                  </p>
                  <LineChart
                    title="Time-weighted return vs benchmark"
                    yFormat={(v) => v.toFixed(0)}
                    series={[
                      {
                        name: "Portfolio",
                        color: "#4e79a7",
                        points: twrSeries.map((p) => ({ ts: p.ts, y: p.index })),
                      },
                      ...(benchmarkSeries.length > 0
                        ? [{
                            name: benchmarkLabel,
                            color: "#e15759",
                            points: benchmarkSeries.map((p) => ({ ts: p.ts, y: p.index })),
                          }]
                        : []),
                    ]}
                  />
                </div>
              </div>

              <div className="card mb-3">
                <div className="card-header fw-bold">
                  Portfolio Value {fx ? `(${fx.base})` : "(per instrument currency, unconverted)"}
                </div>
                <div className="card-body">
                  <LineChart
                    title="Portfolio market value"
                    yFormat={(v) => v.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    series={[{
                      name: "Market value",
                      color: "#59a14f",
                      points: valueSeries.map((p) => ({ ts: p.ts, y: p.value })),
                    }]}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PerformanceView;
